import { useContext, useEffect, useRef } from 'react';
import { Form, FormApi } from 'informed';
import { useTranslation } from 'react-i18next';
import {
  AuthContext,
  CommonContext,
  updatePlayer,
  UpdatePlayerContext,
  useApi,
  User,
} from '@repo-level/core-front';
import {
  DEFAULT_GENDER,
  getGenderOptions,
} from '@repo-level/core-profile/modules/personal/components/helpers';
import { FullInformationFormApi } from 'components/auth/FullInformation';
import { REASON_FOR_UPDATING_USER } from '@repo-level/core-front/contexts/auth/reducer';
import { showTooltip } from 'helpers/tooltip';
import { TooltipTypes } from 'modules/globalTooltip';
import { Select } from 'components/form/Select';
import { FormGroup } from 'components/form/groups/FormGroup';
import { Input } from 'components/form/Input';
import { required } from 'helpers/validators';
import { InputDate } from 'components/form/Date';
import { ScFullNameRow } from '@repo-level/core-profile/modules/personal/components/styled';
import { Button } from 'components/buttons';
import {
  sendOtherModalGameAddDataAppearanceEvent,
  sendOtherModalGameAddDataSubmitEvent,
} from 'modules/googleAnalytics/events/otherEvents';
import { validateDate } from '@repo-level/core-profile/helpers/validators';
import {
  formatDateOfBirth,
  ICompleteProfileFrom,
  ProfileField,
} from './helpers';
import { useCountryOptions } from 'hooks/common';
import { useNavigation } from 'react-navi';
import { usePlayerFieldsStatus } from 'hooks/usePlayerFieldsStatus';

interface ICompleteProfileFormProps {
  gameProvider?: string;
  redirect?: string;
  onClose?: () => void;
}

export const CompleteProfileForm = ({
  gameProvider,
  redirect,
  onClose,
}: ICompleteProfileFormProps) => {
  const { t } = useTranslation(['glossary', 'personal', 'profile', 'common']);
  const { user, fetchUser } = useContext(AuthContext);
  const genderOptions = getGenderOptions(t);
  const { currentIp } = useContext(CommonContext);
  const { navigate } = useNavigation();

  const decodedProvider =
    typeof gameProvider === 'string'
      ? decodeURIComponent(gameProvider)
      : undefined;

  const { missingFields } = usePlayerFieldsStatus(decodedProvider);

  const formApiRef = useRef<FormApi<FullInformationFormApi>>();

  const countryOptions = useCountryOptions();

  const [requestUpdatePlayer, { pending: pendingUpdatePlayer }] = useApi(
    updatePlayer
  );

  const handleSubmit = async ({ ...values }: ICompleteProfileFrom) => {
    try {
      const dateOfBirth = formatDateOfBirth(values.dateOfBirth);
      await requestUpdatePlayer({
        context: UpdatePlayerContext.Edition,
        player: {
          country: values.country || user?.country,
          firstName: values.firstName || user?.firstName,
          lastName: values.lastName || user?.lastName,
          dateOfBirth: dateOfBirth || user?.dateOfBirth,
          gender: values.gender || user?.gender,
        } as Partial<User>,
      }).request.then(async () => {
        const filledFields = Object.keys(values).filter(
          (key) => values[key as keyof ICompleteProfileFrom] !== undefined
        );

        sendOtherModalGameAddDataSubmitEvent(filledFields);

        await fetchUser(REASON_FOR_UPDATING_USER.REGISTRATION);

        showTooltip({
          type: TooltipTypes.SUCCESS,
          title: t('profile:notifications.saveChangesSuccess'),
        });

        onClose && onClose();
        redirect && navigate(redirect);
      });
    } catch (error) {
      console.error(values, error);
      showTooltip({
        type: TooltipTypes.ERROR,
        title: t('profile:notifications.saveChangesError'),
      });
    }
  };

  useEffect(() => {
    sendOtherModalGameAddDataAppearanceEvent(missingFields);
  }, [missingFields]);

  return (
    <>
      {user && (
        <Form<FullInformationFormApi>
          getApi={(api) => {
            formApiRef.current = api;
          }}
          onSubmit={handleSubmit}
        >
          {missingFields.includes(ProfileField.COUNTRY) && (
            <FormGroup title={t('personal:country')}>
              <Select
                field="country"
                placeholder={t('personal:country')}
                initialValue={currentIp?.countryCode}
                options={countryOptions}
                disabled={!!user?.country}
                required
              />
            </FormGroup>
          )}
          {(missingFields.includes(ProfileField.FIRST_NAME) ||
            missingFields.includes(ProfileField.LAST_NAME)) && (
            <>
              <FormGroup title={t('personal:name')}>
                <ScFullNameRow>
                  <Input
                    field="firstName"
                    initialValue={user?.firstName || undefined}
                    placeholder={t('personal:firstName')}
                    validateOnBlur
                    validate={required}
                    disabled={!!user.firstName}
                  />
                  <Input
                    field="lastName"
                    initialValue={user?.lastName || undefined}
                    placeholder={t('personal:lastName')}
                    validateOnBlur
                    validate={required}
                    disabled={!!user.lastName}
                  />
                </ScFullNameRow>
              </FormGroup>
            </>
          )}

          {missingFields.includes(ProfileField.DATE_OF_BIRTH) && (
            <FormGroup title={t('personal:dateOfBirth')}>
              <InputDate
                field="dateOfBirth"
                initialValue={user?.dateOfBirth || undefined}
                disabled={!!user?.dateOfBirth}
                validate={validateDate}
              />
            </FormGroup>
          )}

          {missingFields.includes(ProfileField.GENDER) && (
            <FormGroup title={t('personal:gender')}>
              <Select
                field="gender"
                placeholder={t('personal:gender')}
                initialValue={user?.gender || DEFAULT_GENDER}
                options={genderOptions}
                disabled={!!user?.gender}
              />
            </FormGroup>
          )}

          <div className="submit-container">
            <Button
              type="submit"
              green
              xLarge
              wide
              pending={pendingUpdatePlayer}
            >
              {t('confirm')}
            </Button>
          </div>
        </Form>
      )}
    </>
  );
};
